// src/components/emergencias/ModalEmergencia.js
import { useState, useEffect, useMemo } from "react"
import { X, Save, MapPin, Upload, Trash2, FileText, Eye, Plus } from "lucide-react"
import {
  emergenciasAPI,
  parametrosAPI,
  eventosEmergenciaAPI,
  municipiosAPI,
  departamentosAPI,
  maquinariaAPI,
} from "../../services/apiService"
import { useAzureStorage } from "../../hooks/useAzureStorage"

const convertirFechaParaInput = (fechaString) => {
  if (!fechaString) return ""

  if (fechaString.includes("-") && fechaString.split("-")[0].length <= 2) {
    const [dia, mes, año] = fechaString.split("-")
    return `${año}-${mes.padStart(2, "0")}-${dia.padStart(2, "0")}`
  }

  if (fechaString.includes("T")) {
    return fechaString.split("T")[0]
  }

  return fechaString
}

const convertirFechaParaAPI = (fechaInput) => {
  if (!fechaInput) return null

  try {
    const [year, month, day] = fechaInput.split("-")

    if (!year || !month || !day) {
      console.error("Fecha incompleta:", fechaInput)
      return null
    }

    const date = new Date(Number.parseInt(year), Number.parseInt(month) - 1, Number.parseInt(day), 12, 0, 0)

    if (isNaN(date.getTime())) {
      console.error("Fecha inválida:", fechaInput)
      return null
    }

    return date.toISOString()
  } catch (error) {
    console.error("Error al convertir fecha:", error)
    return null
  }
}

const ModalEmergencia = ({ isOpen, onClose, emergencia = null, onSuccess }) => {
  const isEdit = emergencia !== null

  const { uploadMultipleFiles, isUploadingMultiple, parseAnexoUrls } = useAzureStorage("/7.4-EMERGENCIAS")

  const [formData, setFormData] = useState({
    Fecha: "",
    MunicipioFK: "",
    ZonaCafetalera: "",
    NombreTramo: "",
    LongitudTramo: "",
    TipoEmergenciaFK: "",
    TipoRedFK: "",
    EventoEmergenciaFK: "",
    Estado: "Por Atender",
    Ejecutor: "",
    InversionProyectada: "",
    Latitud: "",
    Longitud: "",
    Observaciones: "",
    Anexo: "",
    MaquinasPrevistas: [],
  })

  const [municipios, setMunicipios] = useState([])
  const [departamentos, setDepartamentos] = useState([])
  const [departamentoSeleccionado, setDepartamentoSeleccionado] = useState("")
  const [tiposEmergencia, setTiposEmergencia] = useState([])
  const [tiposRed, setTiposRed] = useState([])
  const [eventos, setEventos] = useState([])
  const [tiposMaquinaria, setTiposMaquinaria] = useState([])
  const [loading, setLoading] = useState(false)
  const [loadingCatalogos, setLoadingCatalogos] = useState(false)
  const [error, setError] = useState(null)

  const [archivosSeleccionados, setArchivosSeleccionados] = useState([])
  const [anexosExistentes, setAnexosExistentes] = useState([])
  const [uploadProgress, setUploadProgress] = useState(0)

  const municipiosFiltrados = useMemo(() => {
    if (!departamentoSeleccionado) {
      return municipios.sort((a, b) => a.NombreMunicipio.localeCompare(b.NombreMunicipio))
    }
    return municipios
      .filter((mun) => mun.CodigoMunicipio.startsWith(departamentoSeleccionado))
      .sort((a, b) => a.NombreMunicipio.localeCompare(b.NombreMunicipio))
  }, [municipios, departamentoSeleccionado])

  useEffect(() => {
    if (isOpen) {
      loadCatalogos()
      if (emergencia) {
        const fechaConvertida = convertirFechaParaInput(emergencia.Fecha)

        setFormData({
          Fecha: fechaConvertida,
          MunicipioFK: emergencia.MunicipioFK || "",
          ZonaCafetalera: emergencia.ZonaCafetalera || "",
          NombreTramo: emergencia.NombreTramo || "",
          LongitudTramo: emergencia.LongitudTramo || "",
          TipoEmergenciaFK: emergencia.TipoEmergenciaFK || "",
          TipoRedFK: emergencia.TipoRedFK || "",
          EventoEmergenciaFK: emergencia.EventoEmergenciaFK || "",
          Estado: emergencia.Estado || "Por Atender",
          Ejecutor: emergencia.Ejecutor || "",
          InversionProyectada: emergencia.InversionProyectada || "",
          Latitud: emergencia.Latitud || "",
          Longitud: emergencia.Longitud || "",
          Observaciones: emergencia.Observaciones || "",
          Anexo: emergencia.Anexo || "",
          MaquinasPrevistas: emergencia.MaquinasPrevistas?.map((m) => m.TipoMaquinaFK || m.Id) || [],
        })

        if (emergencia.MunicipioFK) {
          const codigoDept = emergencia.MunicipioFK.substring(0, 2)
          setDepartamentoSeleccionado(codigoDept)
        }

        if (emergencia.Anexo) {
          const anexosParsed = parseAnexoUrls(emergencia.Anexo)
          setAnexosExistentes(anexosParsed)
        }
      } else {
        const today = new Date().toISOString().split("T")[0]
        setFormData((prev) => ({ ...prev, Fecha: today }))
        setAnexosExistentes([])
      }
      setArchivosSeleccionados([])
    }
  }, [isOpen, emergencia, parseAnexoUrls])

  const loadCatalogos = async () => {
    setLoadingCatalogos(true)
    setError(null)
    try {
      const [municipiosRes, departamentosRes, tiposEmergenciaRes, tiposRedRes, eventosRes, maquinariaRes] =
        await Promise.all([
          municipiosAPI.getAll().catch((err) => {
            console.error("❌ Error cargando municipios:", err)
            return { success: false, data: [] }
          }),
          departamentosAPI.getAll().catch((err) => {
            console.error("❌ Error cargando departamentos:", err)
            return { success: false, data: [] }
          }),
          parametrosAPI.getTiposEmergencia().catch((err) => {
            console.error("❌ Error cargando tipos emergencia:", err)
            return { success: false, data: [] }
          }),
          parametrosAPI.getTiposRed().catch((err) => {
            console.error("❌ Error cargando tipos red:", err)
            return { success: false, data: [] }
          }),
          eventosEmergenciaAPI.getAll().catch((err) => {
            console.error("❌ Error cargando eventos:", err)
            return { success: false, data: [] }
          }),
          maquinariaAPI.getTipos().catch((err) => {
            console.error("❌ Error cargando tipos de maquinaria:", err)
            return { success: false, data: [] }
          }),
        ])

      const extractData = (response) => {
        if (response && typeof response === "object") {
          if ("success" in response && "data" in response) {
            return response.data
          }
          if (Array.isArray(response)) {
            return response
          }
        }
        return []
      }

      const municipiosData = extractData(municipiosRes)
      const departamentosData = extractData(departamentosRes)
      const tiposEmergenciaData = extractData(tiposEmergenciaRes)
      const tiposRedData = extractData(tiposRedRes)
      const eventosData = extractData(eventosRes)
      const maquinariaData = extractData(maquinariaRes)

      setMunicipios(municipiosData)
      setDepartamentos(departamentosData)
      setTiposEmergencia(tiposEmergenciaData)
      setTiposRed(tiposRedData)
      setEventos(eventosData)
      setTiposMaquinaria(maquinariaData)

      if (municipiosData.length === 0) {
        setError("No se pudieron cargar los municipios. Verifica la conexión con la API.")
      }
    } catch (err) {
      console.error("❌ Error general al cargar catálogos:", err)
      setError("Error al cargar los catálogos necesarios: " + err.message)
    } finally {
      setLoadingCatalogos(false)
    }
  }

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }))
  }

  const handleDepartamentoChange = (e) => {
    setDepartamentoSeleccionado(e.target.value)
    setFormData((prev) => ({ ...prev, MunicipioFK: "" }))
  }

  const handleMaquinariaToggle = (maquinaId) => {
    setFormData((prev) => ({
      ...prev,
      MaquinasPrevistas: prev.MaquinasPrevistas.includes(maquinaId)
        ? prev.MaquinasPrevistas.filter((id) => id !== maquinaId)
        : [...prev.MaquinasPrevistas, maquinaId],
    }))
  }

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files)
    setArchivosSeleccionados((prev) => [...prev, ...files])
  }

  const handleRemoveFile = (index) => {
    setArchivosSeleccionados((prev) => prev.filter((_, i) => i !== index))
  }

  const handleRemoveAnexo = (index) => {
    setAnexosExistentes((prev) => prev.filter((_, i) => i !== index))
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setLoading(true)
    setError(null)
    setUploadProgress(0)

    try {
      const fechaConvertida = convertirFechaParaAPI(formData.Fecha)

      if (!fechaConvertida && formData.Fecha) {
        throw new Error("La fecha ingresada no es válida")
      }

      let anexoFinal = formData.Anexo

      if (archivosSeleccionados.length > 0) {
        console.log("📤 Subiendo archivos a Azure Storage...")
        setUploadProgress(30)

        const uploadResult = await uploadMultipleFiles(archivosSeleccionados, `emergencia_${Date.now()}`)

        if (!uploadResult.success) {
          throw new Error("Error al subir algunos archivos")
        }

        setUploadProgress(70)

        const urlsExistentes = anexosExistentes.map((a) => a.url).filter(Boolean)
        const urlsNuevas = uploadResult.urlsArray || []
        const urlsCombinadas = [...urlsExistentes, ...urlsNuevas]

        anexoFinal = urlsCombinadas.join(",")
        console.log("✅ Anexo final:", anexoFinal)
      } else if (anexosExistentes.length > 0) {
        anexoFinal = anexosExistentes
          .map((a) => a.url)
          .filter(Boolean)
          .join(",")
      }

      setUploadProgress(90)

      const dataParaEnviar = {
        ...formData,
        Fecha: fechaConvertida,
        Anexo: anexoFinal || null,
      }

      if (!dataParaEnviar.TipoRedFK) delete dataParaEnviar.TipoRedFK
      if (!dataParaEnviar.Ejecutor) delete dataParaEnviar.Ejecutor
      if (!dataParaEnviar.Latitud) delete dataParaEnviar.Latitud
      if (!dataParaEnviar.Longitud) delete dataParaEnviar.Longitud
      if (!dataParaEnviar.Anexo) delete dataParaEnviar.Anexo

      console.log("📝 Datos a enviar:", dataParaEnviar)

      if (isEdit) {
        await emergenciasAPI.updateBatch([
          {
            Id: emergencia.Id,
            ...dataParaEnviar,
          },
        ])
      } else {
        await emergenciasAPI.create(dataParaEnviar)
      }

      setUploadProgress(100)
      onSuccess()
      onClose()
    } catch (err) {
      console.error("Error al guardar:", err)
      setError(err.response?.data?.message || err.message || "Error al guardar la emergencia")
    } finally {
      setLoading(false)
      setUploadProgress(0)
    }
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-5xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 p-6 flex items-center justify-between z-10 rounded-t-2xl">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">{isEdit ? "Editar Emergencia" : "Nueva Emergencia"}</h2>
            <p className="text-sm text-gray-600 mt-1">
              {isEdit ? "Modifica los datos de la emergencia" : "Completa todos los campos obligatorios (*)"}
            </p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
            <X className="w-6 h-6 text-gray-500" />
          </button>
        </div>

        {/* Error Alert */}
        {error && (
          <div className="mx-6 mt-4 bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
            <div className="flex-shrink-0">
              <svg className="w-5 h-5 text-red-500" fill="currentColor" viewBox="0 0 20 20">
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
            <div className="flex-1">
              <p className="text-sm font-medium text-red-800">{error}</p>
            </div>
          </div>
        )}

        {/* Upload Progress */}
        {uploadProgress > 0 && (
          <div className="mx-6 mt-4 bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-blue-800">Subiendo archivos...</span>
              <span className="text-sm font-bold text-blue-800">{uploadProgress}%</span>
            </div>
            <div className="w-full bg-blue-200 rounded-full h-2">
              <div
                className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                style={{ width: `${uploadProgress}%` }}
              ></div>
            </div>
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Fecha */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Fecha *</label>
              <input
                type="date"
                name="Fecha"
                value={formData.Fecha}
                onChange={handleChange}
                required
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#c8a88c] focus:border-transparent"
              />
            </div>

            {/* Departamento */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Departamento *</label>
              <select
                value={departamentoSeleccionado}
                onChange={handleDepartamentoChange}
                required
                disabled={loadingCatalogos}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#c8a88c] focus:border-transparent disabled:bg-gray-100"
              >
                <option value="">Seleccionar departamento...</option>
                {departamentos.map((dept) => (
                  <option key={dept.CodigoDepartamento} value={dept.CodigoDepartamento}>
                    {dept.NombreDepartamento}
                  </option>
                ))}
              </select>
            </div>

            {/* Municipio */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Municipio *</label>
              <select
                name="MunicipioFK"
                value={formData.MunicipioFK}
                onChange={handleChange}
                required
                disabled={!departamentoSeleccionado || loadingCatalogos}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#c8a88c] focus:border-transparent disabled:bg-gray-100"
              >
                <option value="">Seleccionar municipio...</option>
                {municipiosFiltrados.map((mun) => (
                  <option key={mun.CodigoMunicipio} value={mun.CodigoMunicipio}>
                    {mun.NombreMunicipio}
                  </option>
                ))}
              </select>
            </div>

            {/* Zona Cafetalera */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Zona Cafetalera</label>
              <input
                type="text"
                name="ZonaCafetalera"
                value={formData.ZonaCafetalera}
                onChange={handleChange}
                placeholder="Nombre de la zona"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#c8a88c] focus:border-transparent"
              />
            </div>

            {/* Nombre Tramo */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Nombre del Tramo</label>
              <input
                type="text"
                name="NombreTramo"
                value={formData.NombreTramo}
                onChange={handleChange}
                placeholder="Ej: Tramo 1"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#c8a88c] focus:border-transparent"
              />
            </div>

            {/* Longitud Tramo */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Longitud del Tramo (km)</label>
              <input
                type="number"
                step="0.01"
                name="LongitudTramo"
                value={formData.LongitudTramo}
                onChange={handleChange}
                placeholder="0.00"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#c8a88c] focus:border-transparent"
              />
            </div>

            {/* Tipo de Emergencia */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Tipo de Emergencia *</label>
              <select
                name="TipoEmergenciaFK"
                value={formData.TipoEmergenciaFK}
                onChange={handleChange}
                required
                disabled={loadingCatalogos}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#c8a88c] focus:border-transparent disabled:bg-gray-100"
              >
                <option value="">Seleccionar...</option>
                {tiposEmergencia.map((tipo) => (
                  <option key={tipo.Id} value={tipo.Id}>
                    {tipo.Nombre}
                  </option>
                ))}
              </select>
            </div>

            {/* Tipo de Red */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Tipo de Red</label>
              <select
                name="TipoRedFK"
                value={formData.TipoRedFK}
                onChange={handleChange}
                disabled={loadingCatalogos}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#c8a88c] focus:border-transparent disabled:bg-gray-100"
              >
                <option value="">Seleccionar...</option>
                {tiposRed.map((tipo) => (
                  <option key={tipo.Id} value={tipo.Id}>
                    {tipo.Nombre}
                  </option>
                ))}
              </select>
            </div>

            {/* Evento/Fenómeno */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Evento/Fenómeno *</label>
              <select
                name="EventoEmergenciaFK"
                value={formData.EventoEmergenciaFK}
                onChange={handleChange}
                required
                disabled={loadingCatalogos}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#c8a88c] focus:border-transparent disabled:bg-gray-100"
              >
                <option value="">Seleccionar...</option>
                {eventos.map((evento) => (
                  <option key={evento.Id} value={evento.Id}>
                    {evento.Fenomeno} ({evento.Año})
                  </option>
                ))}
              </select>
            </div>

            {/* Estado */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Estado</label>
              <select
                name="Estado"
                value={formData.Estado}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#c8a88c] focus:border-transparent"
              >
                <option value="Por Atender">Por Atender</option>
                <option value="En Atención">En Atención</option>
                <option value="Atendido">Atendido</option>
              </select>
            </div>

           {/* Ejecutor */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Ejecutor *</label>
              <select
                name="Ejecutor"
                value={formData.Ejecutor}
                onChange={handleChange}
                required
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#c8a88c] focus:border-transparent"
              >
                <option value="">Seleccionar ejecutor...</option>
                <option value="FCN">FCN</option>
                <option value="CONTRATISTA">CONTRATISTA</option>
                <option value="PRIVADO">PRIVADO</option>
                <option value="GOBIERNO">GOBIERNO</option>
                <option value="ALCALDÍAS">ALCALDÍAS</option>
              </select>
            </div>


            {/* Inversión Proyectada */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Inversión Proyectada (L)</label>
              <input
                type="number"
                step="0.01"
                name="InversionProyectada"
                value={formData.InversionProyectada}
                onChange={handleChange}
                placeholder="0.00"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#c8a88c] focus:border-transparent"
              />
            </div>

            {/* Latitud */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                <MapPin className="w-4 h-4" />
                Latitud
              </label>
              <input
                type="text"
                name="Latitud"
                value={formData.Latitud}
                onChange={handleChange}
                placeholder="14.0723"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#c8a88c] focus:border-transparent"
              />
            </div>

            {/* Longitud */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                <MapPin className="w-4 h-4" />
                Longitud
              </label>
              <input
                type="text"
                name="Longitud"
                value={formData.Longitud}
                onChange={handleChange}
                placeholder="-87.1921"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#c8a88c] focus:border-transparent"
              />
            </div>

            {/* Observaciones */}
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">Observaciones</label>
              <textarea
                name="Observaciones"
                value={formData.Observaciones}
                onChange={handleChange}
                rows="3"
                placeholder="Detalles adicionales sobre la emergencia..."
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#c8a88c] focus:border-transparent resize-none"
              />
            </div>

            {/* Máquinas Previstas */}
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-3 flex items-center gap-2">
                <Plus className="w-4 h-4" />
                Máquinas Previstas
              </label>

              {tiposMaquinaria.length > 0 ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {tiposMaquinaria.map((maquina) => (
                    <label
                      key={maquina.Id}
                      className="flex items-center p-3 border border-gray-300 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors"
                    >
                      <input
                        type="checkbox"
                        checked={formData.MaquinasPrevistas.includes(maquina.Id)}
                        onChange={() => handleMaquinariaToggle(maquina.Id)}
                        className="w-4 h-4 text-[#c8a88c] rounded focus:ring-2 focus:ring-[#c8a88c]"
                      />
                      <span className="ml-3 text-sm font-medium text-gray-700">{maquina.Tipo}</span>
                    </label>
                  ))}
                </div>
              ) : (
                <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg text-center">
                  <p className="text-sm text-gray-600">
                    {loadingCatalogos ? "Cargando tipos de maquinaria..." : "No hay tipos de maquinaria disponibles"}
                  </p>
                </div>
              )}

              {/* Display selected machinery */}
              {formData.MaquinasPrevistas.length > 0 && (
                <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <p className="text-xs font-medium text-blue-800 mb-2">
                    {formData.MaquinasPrevistas.length} máquina(s) seleccionada(s)
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {formData.MaquinasPrevistas.map((maquinaId) => {
                      const maquina = tiposMaquinaria.find((m) => m.Id === maquinaId)
                      return (
                        <span
                          key={maquinaId}
                          className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-800 text-xs font-medium rounded"
                        >
                          {maquina?.Tipo}
                          <button
                            type="button"
                            onClick={() => handleMaquinariaToggle(maquinaId)}
                            className="ml-1 text-blue-600 hover:text-blue-800"
                          >
                            ×
                          </button>
                        </span>
                      )
                    })}
                  </div>
                </div>
              )}
            </div>

            {/* Anexos y Documentos */}
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                <FileText className="w-4 h-4" />
                Anexos y Documentos
              </label>

              {/* Input de archivos */}
              <div className="mb-3">
                <label className="flex items-center justify-center w-full px-4 py-6 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-[#c8a88c] hover:bg-gray-50 transition-colors">
                  <div className="text-center">
                    <Upload className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                    <p className="text-sm text-gray-600">
                      <span className="font-semibold text-[#c8a88c]">Click para subir</span> o arrastra archivos aquí
                    </p>
                    <p className="text-xs text-gray-500 mt-1">PDF, Word, Excel, Imágenes (máx. 50MB)</p>
                  </div>
                  <input
                    type="file"
                    multiple
                    onChange={handleFileSelect}
                    className="hidden"
                    accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.gif"
                  />
                </label>
              </div>

              {/* Anexos existentes */}
              {anexosExistentes.length > 0 && (
                <div className="mb-3">
                  <p className="text-xs font-medium text-gray-600 mb-2">Archivos ya subidos:</p>
                  <div className="space-y-2">
                    {anexosExistentes.map((anexo, index) => (
                      <div
                        key={anexo.id}
                        className="flex items-center justify-between p-3 bg-green-50 border border-green-200 rounded-lg"
                      >
                        <div className="flex items-center gap-3 flex-1 min-w-0">
                          <span className="text-xl">{anexo.icon}</span>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-900 truncate">{anexo.fileName}</p>
                            <p className="text-xs text-gray-500">Ya subido</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <a
                            href={anexo.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                            title="Ver archivo"
                          >
                            <Eye className="w-4 h-4" />
                          </a>
                          <button
                            type="button"
                            onClick={() => handleRemoveAnexo(index)}
                            className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                            title="Eliminar"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Archivos seleccionados para subir */}
              {archivosSeleccionados.length > 0 && (
                <div>
                  <p className="text-xs font-medium text-gray-600 mb-2">Archivos nuevos a subir:</p>
                  <div className="space-y-2">
                    {archivosSeleccionados.map((file, index) => (
                      <div
                        key={index}
                        className="flex items-center justify-between p-3 bg-blue-50 border border-blue-200 rounded-lg"
                      >
                        <div className="flex items-center gap-3 flex-1 min-w-0">
                          <FileText className="w-5 h-5 text-blue-600" />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-900 truncate">{file.name}</p>
                            <p className="text-xs text-gray-500">{(file.size / 1024).toFixed(2)} KB</p>
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => handleRemoveFile(index)}
                          className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                          title="Eliminar"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Footer */}
          <div className="flex justify-end gap-3 mt-8 pt-6 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 transition-colors"
              disabled={loading || isUploadingMultiple}
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={loading || loadingCatalogos || isUploadingMultiple}
              className="flex items-center gap-2 px-6 py-2 bg-[#c8a88c] text-white rounded-lg font-medium hover:bg-[#b8987c] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {loading || isUploadingMultiple ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                  {isUploadingMultiple ? "Subiendo..." : "Guardando..."}
                </>
              ) : (
                <>
                  <Save className="w-5 h-5" />
                  {isEdit ? "Actualizar" : "Guardar"}
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default ModalEmergencia
